<script lang="ts">
  import { router, Link } from '@inertiajs/svelte';
  import type { BoardItem, BoardSummary, Project } from '../../lib/types';
  import momentiumLogo from '../../assets/momentium-logo.png';
  import NavDropdown from '../../components/NavDropdown.svelte';
  import ProfileDropdown from '../../components/ProfileDropdown.svelte';
  import MobileTopBar from '../../components/mobile/MobileTopBar.svelte';
  import { viewport } from '../../lib/stores/viewport';
  import { projectUpdated } from '../../lib/stores/ui';
  import ProjectRail from '../../components/ProjectRail.svelte';
  import ProjectModal from '../../components/ProjectModal.svelte';
  import BoardCanvas from '../../components/board/BoardCanvas.svelte';
  import BoardReadOnlyCanvas from '../../components/board/BoardReadOnlyCanvas.svelte';
  import MobileBoardView from '../../components/mobile/MobileBoardView.svelte';
  import ActionSheet from '../../components/mobile/ActionSheet.svelte';
  import MobileSheet from '../../components/MobileSheet.svelte';
  import Icon from '../../components/Icon.svelte';

  interface Props {
    board: BoardSummary;
    items: BoardItem[];
    projects: Project[];
  }

  let { board, items, projects }: Props = $props();

  let renaming = $state(false);
  // Seeded from the prop; startRename() re-reads the current name each time.
  // svelte-ignore state_referenced_locally
  let draftName = $state(board.name);

  // The page ships only the projects placed on the board; the canvas reports
  // projects it adds/restores after load so the rail can open them without a
  // reload. Keep a live merged list for the rail (and the canvas seed). Seeded
  // from the prop once — updates arrive via onProjectsKnown, not prop changes.
  // svelte-ignore state_referenced_locally
  let liveProjects = $state(projects);

  // Edits made in the project rail / modal (title, status, …) arrive through this store. The
  // canvas applies them to its nodes, but `liveProjects` feeds the rail and the phone list too;
  // left stale, the rail's Edit form would re-open with the old values and save them back.
  // eslint-disable-next-line momentium/no-unguarded-effect -- external store subscription for project edits, returns cleanup
  $effect(() =>
    projectUpdated.subscribe((updated) => {
      if (!updated || !liveProjects.some((p) => p.id === updated.id)) return;
      liveProjects = liveProjects.map((p) => (p.id === updated.id ? { ...p, ...updated } : p));
    }),
  );

  function handleProjectsKnown(learned: Project[]) {
    const known = new Map(liveProjects.map((p) => [p.id, p]));
    for (const project of learned) known.set(project.id, project);
    liveProjects = [...known.values()];
  }

  function startRename() {
    draftName = board.name;
    renaming = true;
  }

  // Phones (ma-inzp): no canvas editing. The list is the page; the read-only canvas
  // is an overlay on top of it; rename (a board property, not canvas editing) is
  // reached from the top bar's ⋯.
  let canvasOpen = $state(false);
  let menuOpen = $state(false);
  let renameSheetOpen = $state(false);

  function openRenameSheet() {
    draftName = board.name;
    renameSheetOpen = true;
  }

  function submitMobileRename(event: SubmitEvent) {
    event.preventDefault();
    renameSheetOpen = false;
    submitRename(event);
  }

  function submitRename(event: SubmitEvent) {
    event.preventDefault();
    const name = draftName.trim();
    renaming = false;
    if (!name || name === board.name) return;

    router.patch(`/boards/${board.id}`, { board: { name } }, { preserveScroll: true });
  }
</script>

<svelte:head><title>{board.name} · Boards · Momentium</title></svelte:head>

<div class="board-page">
  {#if $viewport.isMobile}
    <MobileTopBar title={board.name} back="/boards" backLabel="Back to Boards">
      {#snippet actions()}
        <button
          type="button"
          class="mtb-icon-btn"
          aria-label="Board actions"
          aria-haspopup="dialog"
          onclick={() => (menuOpen = true)}
        >
          <Icon name="more" size={20} />
        </button>
      {/snippet}
    </MobileTopBar>
  {:else}
    <header class="board-header">
      <div class="header-left">
        <Link href="/" class="logo">
          <img src={momentiumLogo} alt="Momentium" class="logo-icon" />
        </Link>
        <NavDropdown activePage="boards" />
        <span class="crumb-sep">/</span>
        <Link href="/boards" class="crumb">Boards</Link>
        <span class="crumb-sep">/</span>

        {#if renaming}
          <form onsubmit={submitRename}>
            <!-- svelte-ignore a11y_autofocus -->
            <input
              autocomplete="off"
              bind:value={draftName}
              class="name-input"
              type="text"
              autofocus
              aria-label="Board name"
              onblur={() => (renaming = false)}
            />
          </form>
        {:else}
          <button class="board-name" type="button" onclick={startRename} title="Rename board">
            {board.name}
          </button>
        {/if}
      </div>

      <ProfileDropdown />
    </header>
  {/if}

  {#if $viewport.isMobile}
    <div class="mobile-body" inert={canvasOpen}>
      <MobileBoardView {items} projects={liveProjects} onViewCanvas={() => (canvasOpen = true)} />
    </div>

    {#if canvasOpen}
      <BoardReadOnlyCanvas
        {board}
        {items}
        projects={liveProjects}
        onClose={() => (canvasOpen = false)}
      />
    {/if}

    <ActionSheet
      open={menuOpen}
      onClose={() => (menuOpen = false)}
      title={board.name}
      actions={[{ label: 'Rename board', icon: 'edit', onSelect: openRenameSheet }]}
    />

    <MobileSheet
      open={renameSheetOpen}
      onClose={() => (renameSheetOpen = false)}
      direction="bottom"
      title="Rename board"
    >
      <form class="rename-form" onsubmit={submitMobileRename}>
        <input
          autocomplete="off"
          bind:value={draftName}
          class="rename-input"
          type="text"
          aria-label="Board name"
          data-sheet-autofocus
        />
        <button class="rename-save" type="submit" disabled={!draftName.trim()}>Save</button>
      </form>
    </MobileSheet>
  {:else}
    <div class="canvas-shell">
      <BoardCanvas {board} {items} projects={liveProjects} onProjectsKnown={handleProjectsKnown} />
    </div>
  {/if}

  <!-- Overlay the rail so opening project details does not reflow the canvas. -->
  <ProjectRail allProjects={liveProjects} floating />
  <ProjectModal />
</div>

<style>
  .board-page {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .board-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.375rem 0.75rem;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  :global(.board-header .logo) {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
  }

  .logo-icon {
    width: 1.25rem;
    height: 1.25rem;
  }

  .crumb-sep {
    color: #d1d5db;
    font-size: 0.75rem;
  }

  :global(.board-header .crumb) {
    font-size: 0.75rem;
    color: #6b7280;
    text-decoration: none;
  }

  :global(.board-header .crumb:hover) {
    color: #2563eb;
  }

  .board-name {
    font-size: 0.75rem;
    font-weight: 600;
    color: #1f2937;
    background: none;
    border: none;
    font-family: inherit;
    cursor: text;
    padding: 0.125rem 0.25rem;
    border-radius: 0.1875rem;
  }

  .board-name:hover {
    background: #f3f4f6;
  }

  .name-input {
    font-size: 0.75rem;
    font-weight: 600;
    font-family: inherit;
    color: #1f2937;
    padding: 0.125rem 0.25rem;
    border: 1px solid #2563eb;
    border-radius: 0.1875rem;
    outline: none;
  }

  .canvas-shell {
    flex: 1;
    min-height: 0;
  }

  .mobile-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 24px;
  }

  .rename-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 4px 16px 16px;
  }

  .rename-input {
    min-height: 48px;
    padding: 0 12px;
    border: 1px solid #d1d5db;
    border-radius: 12px;
    font: inherit;
    font-size: 16px;
  }

  .rename-input:focus {
    outline: none;
    border-color: #2563eb;
  }

  .rename-save {
    min-height: 48px;
    border: 0;
    border-radius: 12px;
    background: #2563eb;
    color: #fff;
    font: inherit;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
  }

  .rename-save:disabled {
    background: #cbd5e1;
  }
</style>
